import Image from '@atoms/Image/Image';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import { IMessage } from '@models/IMessage';
import { IPricingData } from '@models/IPricingData';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import { IProductTrackingData } from '@models/IProductTrackingData';
import ProductStockStatusEnum from '@models/enums/ProductStockStatusEnum';
import ProductDetailsCta from '@molecules/ProductDetailsCta/ProductDetailsCta';
import ProductDetailsStockStatus from '@molecules/ProductDetailsStockStatus/ProductDetailsStockStatus';
import useComponentsHeightsStore from '@state/ComponentsHeightsStore';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import { useEffect } from 'react';

interface Props {
  readonly backOrderText: IMessage;
  readonly buyNowLabel?: string;
  readonly buyNowUrl?: string;
  readonly className?: string;
  readonly dealerFindLink: ILink;
  readonly deliveryLabel: string;
  readonly disableFindDealerCta?: boolean;
  readonly image?: IImage;
  readonly name: string;
  readonly notifyMeContactFormEndpoint: string;
  readonly preOrderText: IMessage;
  readonly pricingData?: IPricingData;
  readonly skuCode: string;
  readonly statusLabels: IProductListingStatusLabels;
  readonly trackingData?: IProductTrackingData;
  readonly onAddProductToCart: () => Promise<void>;
}

const ProductDetailsStickyBuyBar = ({
  backOrderText,
  buyNowLabel,
  buyNowUrl,
  className,
  dealerFindLink,
  deliveryLabel,
  disableFindDealerCta,
  image,
  name,
  notifyMeContactFormEndpoint,
  preOrderText,
  pricingData,
  skuCode,
  statusLabels,
  trackingData,
  onAddProductToCart,
}: Props) => {
  const { setStickyBuyBarHeight } = useComponentsHeightsStore((state) => ({
    setStickyBuyBarHeight: state.setStickyBuyBarHeight,
  }));
  const [stickyBuyBarRef, { clientHeight: stickyBuyBarHeight }] =
    useResizeObserver();

  useEffect(() => {
    setStickyBuyBarHeight(stickyBuyBarHeight);
  }, [stickyBuyBarHeight, setStickyBuyBarHeight]);

  const { formattedCalculatedPrice, amountSaved, formattedPrice, status } =
    pricingData || ({} as IPricingData);
  const isPriceDisplayed =
    formattedCalculatedPrice || (amountSaved > 0 && formattedPrice);

  return (
    <div
      ref={stickyBuyBarRef}
      className="w-full border-grey-400 border-t bg-white px-5 py-[15px] lg:px-5 lg:py-5 xl:px-13"
      data-testid="CT-163-Sticky-Buy-Bar"
    >
      <div
        className={classNames(
          'flex items-center max-lg:flex-col max-lg:gap-y-2.5 lg:justify-between lg:gap-x-4',
          className
        )}
      >
        <div className="flex items-center max-lg:w-full xl:gap-x-13">
          {image && (
            <Image
              image={image}
              imageClasses="object-contain h-[68px] w-auto"
              classes="max-xl:hidden"
              dataTestId="CT-163-Sticky-Buy-Bar-Image"
            />
          )}

          <div className="max-lg:flex max-lg:w-full max-lg:justify-between max-lg:gap-x-2.5">
            <p
              className="body-two max-lg:max-w-[60%]"
              data-testid="CT-163-Sticky-Buy-Bar-Product-Name"
            >
              {name}
            </p>

            {isPriceDisplayed && (
              <p className="flex flex-wrap-reverse gap-x-2.5 max-lg:justify-end">
                {formattedCalculatedPrice && (
                  <span
                    className="body-three body-three--bold max-lg:text-right"
                    data-testid="CT-163-Sticky-Buy-Bar-Formatted-Calculated-Price"
                  >
                    {formattedCalculatedPrice}
                  </span>
                )}

                {amountSaved > 0 && formattedPrice && (
                  <span
                    className="body-three body-three--bold text-grey-450 line-through max-lg:text-right"
                    data-testid="CT-163-Sticky-Buy-Bar-Formatted-Price"
                  >
                    {formattedPrice}
                  </span>
                )}
              </p>
            )}
          </div>
        </div>

        <div className="flex max-lg:w-full max-lg:flex-col max-lg:gap-y-2.5 lg:max-w-[600px] lg:items-center lg:gap-x-5 xl:max-w-full xl:gap-x-13">
          <div className="max-lg:self-end lg:max-w-[350px] lg:shrink-0">
            <ProductDetailsStockStatus
              dataTestId="CT-163-Sticky-Buy-Bar-Stock-Status"
              className="max-lg:justify-end"
              deliveryLabel={deliveryLabel}
              backOrderText={backOrderText}
              preOrderText={preOrderText}
              status={status}
              statusLabels={statusLabels}
              onAddProductToCart={onAddProductToCart}
            />

            {status === ProductStockStatusEnum.InStock && (
              <p
                className="body-four max-lg:text-right"
                data-testid="CT-163-Sticky-Buy-Bar-Delivery-Label"
              >
                {deliveryLabel}
              </p>
            )}
          </div>

          <ProductDetailsCta
            backOrderText={backOrderText}
            buyNowLabel={buyNowLabel}
            buyNowUrl={buyNowUrl}
            dataTestId="CT-163-Sticky-Buy-Bar-CTA"
            directionClassName="md:flex-row"
            disableFindDealerCta={disableFindDealerCta}
            dealerFindLink={dealerFindLink}
            gapClassName="gap-x-5 gap-y-2.5"
            notifyMeContactFormEndpoint={notifyMeContactFormEndpoint}
            preOrderText={preOrderText}
            pricingData={pricingData}
            sharedButtonsClassName="max-md:w-full text-center grow"
            skuCode={skuCode}
            statusLabels={statusLabels}
            trackingData={trackingData}
            onAddProductToCart={onAddProductToCart}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsStickyBuyBar;
